import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react'
import { useClientContext } from './ClientContext'
import { useAuth } from './AuthContext'
import type { Client, Project } from '../api/generated/models'

export interface ClientProject {
  client: Client
  project: Project
  /** Active Solution.id values from GET /api/v2/projects/ */
  active_solution_ids: number[]
}

interface ProjectLiteRow {
  id: number
  name?: string
  logo?: string | null
  status?: string
  client_id: number
  client?: {
    id?: number
    name?: string
    logo?: string | null
    is_active?: boolean
  }
  active_solution_ids?: number[]
}

interface ClientProjectContextType {
  clientProjectData: ClientProject[]
  setClientProjectData: (data: ClientProject[]) => void
  refreshClientProjects: () => Promise<void>
}

interface ClientProjectProviderProps {
  children: ReactNode
}

const ClientProjectContext = createContext<
  ClientProjectContextType | undefined
>(undefined)

export const ClientProjectProvider: React.FC<ClientProjectProviderProps> = ({
  children,
}) => {
  const [clientProjectData, setClientProjectData] = useState<ClientProject[]>(
    [],
  )
  const { isAuthenticated, user, isAdmin, isClientUser, isClientManager } =
    useAuth()
  const { selectedClient } = useClientContext()

  const refreshClientProjects = useCallback(async () => {
    if (!isAuthenticated) return

    try {
      const projectsResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v2/projects/`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        },
      )

      if (!projectsResponse.ok) {
        throw new Error(`Failed to fetch projects`)
      }

      const projectsResult = await projectsResponse.json()
      const projects = (projectsResult.data as ProjectLiteRow[]) ?? []

      // Map lite /projects/v2 rows into Client/Project shapes expected by pickers.
      // Inactive projects stay selectable so users can view saved models/reports.
      const clientProjects: ClientProject[] = projects.map((project) => ({
        client: {
          id: project.client_id,
          name: project.client?.name ?? '',
          logo: project.client?.logo ?? null,
          client_id: '',
          kam_id: 0,
          is_active: project.client?.is_active,
        },
        project: {
          id: project.id,
          name: project.name ?? '',
          logo: project.logo ?? null,
          client_id: project.client_id,
          kam_id: 0,
          project_id: '',
          status: project.status as Project['status'],
        },
        active_solution_ids: project.active_solution_ids ?? [],
      }))

      setClientProjectData(clientProjects)
    } catch (error) {
      console.error('Error fetching client-project data:', error)
    }
  }, [isAuthenticated])

  useEffect(() => {
    refreshClientProjects()
  }, [
    refreshClientProjects,
    user,
    isAdmin,
    isClientUser,
    isClientManager,
    selectedClient,
  ])

  return (
    <ClientProjectContext.Provider
      value={{
        clientProjectData,
        setClientProjectData,
        refreshClientProjects,
      }}
    >
      {children}
    </ClientProjectContext.Provider>
  )
}

export const projectAllowsModelRun = (
  project?: { status?: string | null } | null,
) => project?.status !== 'inactive'

export const selectedProjectAllowsModelRun = (
  selectedProject: { id?: number; status?: string | null } | null,
  clientProjectData: ClientProject[],
) => {
  const live = clientProjectData.find(
    (row) => Number(row.project.id) === Number(selectedProject?.id),
  )?.project
  return projectAllowsModelRun(live ?? selectedProject)
}

export const projectPickerLabel = (
  name: string | undefined,
  status?: string | null,
) => (status === 'inactive' ? `${name ?? ''} (inactive)` : (name ?? ''))

export const clientPickerLabel = (
  name: string | undefined,
  isActive?: boolean | null,
) => (isActive === false ? `${name ?? ''} (inactive)` : (name ?? ''))

export const useClientProjectContext = (): ClientProjectContextType => {
  const context = useContext(ClientProjectContext)

  if (context === undefined) {
    throw new Error(
      'useClientProjectContext must be used within a ClientProjectProvider',
    )
  }

  return context
}
